import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { Add as AddIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { GridRowSelectionModel } from '@mui/x-data-grid';
import { skillsAPI, templateAPI } from '@/services/api';
import type { LicenseKey, LicenseTemplate } from '../types';
import {
  LicenseTable,
  LicenseCreateDialog,
  LicenseBatchDialog,
  LicenseEditDialog,
} from '../components';
import { useLicenses, useLicenseSkills } from '../hooks';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * 许可证管理页面 - 精简版本（组合容器）
 */
export default function LicenseKeys() {
  const { t } = useTranslation();

  // 数据管理
  const {
    licenses,
    loading,
    error,
    refresh,
    create,
    update,
    batchCreate,
    batchDeactivate,
    toggleActivation,
  } = useLicenses();

  // 技能关联管理
  const {
    availableSkills,
    selectedSkills,
    loading: loadingSkills,
    loadForCreate,
    loadForLicense,
    toggleSkill,
    reset: resetSkills,
  } = useLicenseSkills();

  // UI 状态
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<LicenseKey | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [tabValue, setTabValue] = useState(0);

  // 通知状态
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState('');

  // 模板状态
  const [templates, setTemplates] = useState<LicenseTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // 初始化加载
  useEffect(() => {
    refresh();
  }, [refresh]);

  // 加载模板
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await templateAPI.getAll();
      setTemplates(response.data || []);
    } catch (requestError) {
      console.error('Failed to load templates:', requestError);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // 打开创建对话框
  const handleOpenCreateDialog = useCallback(async () => {
    resetSkills();
    await Promise.all([loadForCreate(), loadTemplates()]);
    setTabValue(0);
    setDialogOpen(true);
  }, [resetSkills, loadForCreate, loadTemplates]);

  // 打开编辑对话框
  const handleOpenEditDialog = useCallback(async (license: LicenseKey) => {
    setEditingLicense(license);
    setEditDialogOpen(true);
    await loadForLicense(license.key);
  }, [loadForLicense]);

  // 复制到剪贴板
  const copyToClipboard = useCallback(async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setSuccessMessage(t('licenses.copiedToClipboard'));
    } catch {
      setLocalError('Failed to copy to clipboard.');
    }
  }, [t]);

  // 切换激活状态
  const handleToggleActivation = useCallback(async (id: number, currentState: boolean) => {
    const nextState = !currentState;
    const confirmMessage = nextState
      ? t('licenses.confirmActivate')
      : t('licenses.confirmDeactivate');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await toggleActivation(id, currentState);
      setSuccessMessage(nextState ? t('licenses.activatedSuccess') : t('licenses.deactivatedSuccess'));
      refresh();
    } catch (requestError) {
      console.error('Failed to toggle license activation:', requestError);
      setLocalError(nextState ? t('licenses.activateFailed') : t('licenses.deactivateFailed'));
    }
  }, [toggleActivation, refresh, t]);

  // 批量停用
  const handleBatchDeactivate = useCallback(async () => {
    const selectedLicenseIds = selectionModel.filter((id): id is number => typeof id === 'number');

    if (selectedLicenseIds.length === 0) {
      setLocalError(t('licenses.noLicenses'));
      return;
    }

    if (!window.confirm(t('licenses.confirmBatchDeactivate', { count: selectedLicenseIds.length }))) {
      return;
    }

    try {
      await batchDeactivate(selectedLicenseIds);
      setSuccessMessage(t('licenses.batchDeactivatedSuccess', { count: selectedLicenseIds.length }));
      setSelectionModel([]);
      refresh();
    } catch (requestError: any) {
      setLocalError(requestError.response?.data?.error || t('licenses.deactivateFailed'));
    }
  }, [selectionModel, batchDeactivate, refresh, t]);

  // 创建许可证
  const handleCreate = useCallback(async (payload: any) => {
    return create(payload);
  }, [create]);

  // 批量创建许可证
  const handleBatchCreate = useCallback(async (payload: any) => {
    return batchCreate(payload);
  }, [batchCreate]);

  // 更新许可证
  const handleUpdate = useCallback(async (id: number, payload: any) => {
    return update(id, payload);
  }, [update]);

  // 关联技能
  const handleAssociateSkills = useCallback(async (licenseKey: string, skillIds: string[]) => {
    await skillsAPI.updateLicenseSkills(licenseKey, skillIds);
  }, []);

  // 成功回调
  const handleSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
  }, []);

  // 错误回调
  const handleError = useCallback((message: string) => {
    setLocalError(message);
  }, []);

  // 关闭创建对话框
  const handleCloseCreateDialog = useCallback(() => {
    setDialogOpen(false);
    setTabValue(0);
  }, []);

  // 关闭编辑对话框
  const handleCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setEditingLicense(null);
  }, []);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">{t('licenses.title')}</Typography>
      </Box>

      {(error || localError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError('')}>
          {error || localError}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" gap={1}>
          {selectionModel.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleBatchDeactivate}
            >
              {t('licenses.deactivate')} ({selectionModel.length})
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            {t('licenses.createLicenseKey')}
          </Button>
        </Box>
      </Box>

      <LicenseTable
        licenses={licenses}
        loading={loading}
        selectionModel={selectionModel}
        onSelectionChange={setSelectionModel}
        onEdit={handleOpenEditDialog}
        onToggleActivation={handleToggleActivation}
        onCopyKey={copyToClipboard}
      />

      {/* 创建对话框（带 Tab 切换） */}
      <Dialog open={dialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>{t('licenses.createTitle')}</DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} sx={{ mb: 2 }}>
            <Tab label={t('licenses.singleCreate')} />
            <Tab label={t('licenses.batchCreateTitle')} />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <LicenseCreateDialog
              open={dialogOpen && tabValue === 0}
              onClose={handleCloseCreateDialog}
              onSuccess={handleSuccess}
              onError={handleError}
              onCreate={handleCreate}
              onAssociateSkills={handleAssociateSkills}
              availableSkills={availableSkills}
              selectedSkills={selectedSkills}
              loadingSkills={loadingSkills}
              onToggleSkill={toggleSkill}
              onRefresh={refresh}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <LicenseBatchDialog
              open={dialogOpen && tabValue === 1}
              onClose={handleCloseCreateDialog}
              onSuccess={handleSuccess}
              onError={handleError}
              onBatchCreate={handleBatchCreate}
              onAssociateSkills={handleAssociateSkills}
              availableSkills={availableSkills}
              selectedSkills={selectedSkills}
              loadingSkills={loadingSkills}
              onToggleSkill={toggleSkill}
              templates={templates}
              loadingTemplates={loadingTemplates}
              onRefresh={refresh}
            />
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>{t('licenses.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* 编辑对话框 */}
      <LicenseEditDialog
        open={editDialogOpen}
        license={editingLicense}
        onClose={handleCloseEditDialog}
        onSuccess={handleSuccess}
        onError={handleError}
        onUpdate={handleUpdate}
        onAssociateSkills={handleAssociateSkills}
        availableSkills={availableSkills}
        selectedSkills={selectedSkills}
        loadingSkills={loadingSkills}
        onToggleSkill={toggleSkill}
        onRefresh={refresh}
      />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Box>
  );
}
